// src/SuperBilling.js
import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from './api';
import {
  FileText,
  RefreshCw,
  Send,
  PlusCircle,
  CheckCircle,
  AlertTriangle,
  Filter,
  XCircle,
  ChevronDown,
  ChevronRight,
  Info,
  Download,          // <-- NOVO
} from 'lucide-react';

const SuperBilling = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [filters, setFilters] = useState({
    organization_id: '',
    status: '',
    date_from: '',
    date_to: '',
  });
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [showNewModal, setShowNewModal] = useState(false);
  const [newForm, setNewForm] = useState({
    organization_id: '',
    period_start: '',
    period_end: '',
    invoice_type: 'subscription',
    auto_calculate: true,
    amount: '',
    notes: '',
  });
  const [creating, setCreating] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);
  const [autoSummary, setAutoSummary] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [markingPaidId, setMarkingPaidId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // NOVO: state za ZIP export
  const [exportingZip, setExportingZip] = useState(false);

  // --- LOAD ORGS ---
  const loadOrgs = async () => {
    try {
      const res = await API.get('/super/dashboard');
      const list = res.data?.org_list || [];
      setOrgs(list);
    } catch (e) {
      console.error('loadOrgs error', e);
    }
  };

  // --- LOAD INVOICES ---
  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMsg('');
      const res = await API.get('/super/invoices', { params: filters });
      setInvoices(res.data || []);
    } catch (e) {
      console.error('loadInvoices error', e);
      setError('Failed to load invoices.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrgs();
  }, []);

  // kad se promene filteri – učitaj fakture
  useEffect(() => {
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.organization_id, filters.status, filters.date_from, filters.date_to]);

  // --- PRIMENA org_id IZ QUERY STRINGA ---
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orgIdFromQuery = params.get('org_id');

    if (orgIdFromQuery) {
      setFilters((prev) => {
        if (prev.organization_id === orgIdFromQuery) return prev;
        return { ...prev, organization_id: orgIdFromQuery };
      });
    }
  }, [location.search]);

  // --- FILTERS ---
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      organization_id: '',
      status: '',
      date_from: '',
      date_to: '',
    });
  };

  // --- NEW INVOICE MODAL ---
  const openNewModal = () => {
    setNewForm({
      organization_id: filters.organization_id || '',
      period_start: '',
      period_end: '',
      invoice_type: 'subscription',
      auto_calculate: true,
      amount: '',
      notes: '',
    });
    setError('');
    setSuccessMsg('');
    setShowNewModal(true);
  };

  const handleNewChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const submitNewInvoice = async (e) => {
    e.preventDefault();
    if (!newForm.organization_id || !newForm.period_start || !newForm.period_end) return;

    try {
      setCreating(true);
      setError('');
      setSuccessMsg('');
      const payload = {
        organization_id: Number(newForm.organization_id),
        period_start: newForm.period_start,
        period_end: newForm.period_end,
        invoice_type: newForm.invoice_type,
        auto_calculate: newForm.auto_calculate,
        notes: newForm.notes || undefined,
      };
      if (!newForm.auto_calculate && newForm.amount) {
        payload.amount = Number(newForm.amount);
      }
      await API.post('/super/invoices', payload);
      setShowNewModal(false);
      setSuccessMsg('Invoice created successfully.');
      await loadInvoices();
    } catch (e) {
      console.error('create invoice error', e);
      setError('Failed to create invoice.');
    } finally {
      setCreating(false);
    }
  };

  // --- AUTO BILLING ---
  const runAutoBilling = async () => {
    try {
      setAutoRunning(true);
      setError('');
      setSuccessMsg('');
      setAutoSummary(null);
      const res = await API.post('/super/invoices/run-auto');
      const summary = res.data?.summary || null;
      setAutoSummary(summary);
      if (summary) {
        setSuccessMsg(
          `Auto billing completed: generated ${summary.generated || 0} invoices, errors ${
            summary.errors || 0
          }.`
        );
      }
      await loadInvoices();
    } catch (e) {
      console.error('run-auto error', e);
      setError('Auto billing failed.');
    } finally {
      setAutoRunning(false);
    }
  };

  // --- EMAIL SEND ---
  const sendEmail = async (id) => {
    try {
      setSendingId(id);
      setError('');
      setSuccessMsg('');
      await API.post(`/super/invoices/${id}/send-email`, {});
      setSuccessMsg('Invoice email sent.');
      await loadInvoices();
    } catch (e) {
      console.error('send-email error', e);
      setError('Failed to send invoice email.');
    } finally {
      setSendingId(null);
    }
  };

  // --- MARK PAID ---
  const markPaid = async (id) => {
    try {
      setMarkingPaidId(id);
      setError('');
      setSuccessMsg('');
      await API.patch(`/super/invoices/${id}/status`, {
        status: 'paid',
      });
      setSuccessMsg('Invoice marked as paid.');
      await loadInvoices();
    } catch (e) {
      console.error('mark-paid error', e);
      setError('Failed to update invoice status.');
    } finally {
      setMarkingPaidId(null);
    }
  };

  // --- MAP ORG EMAIL STATUS + DETALJI ---
  const orgEmailMap = useMemo(() => {
    const map = {};
    orgs.forEach((o) => {
      const hasEmail = !!(o?.billing_email || o?.contact_email);
      map[o.id] = hasEmail;
    });
    return map;
  }, [orgs]);

  const orgEmailDetails = useMemo(() => {
    const map = {};
    orgs.forEach((o) => {
      map[o.id] = {
        billing: o?.billing_email || '',
        contact: o?.contact_email || '',
      };
    });
    return map;
  }, [orgs]);

  // --- STATS ---
  const stats = useMemo(() => {
    if (!invoices || invoices.length === 0) {
      return {
        totalAmount: 0,
        totalPaid: 0,
        totalUnpaid: 0,
        overdueCount: 0,
        draftCount: 0,
      };
    }
    let totalAmount = 0;
    let totalPaid = 0;
    let overdueCount = 0;
    let draftCount = 0;

    invoices.forEach((inv) => {
      const amt = Number(inv.amount || 0);
      totalAmount += amt;
      if (inv.status === 'paid') {
        totalPaid += amt;
      }
      if (inv.status === 'overdue') {
        overdueCount++;
      }
      if (inv.status === 'draft') {
        draftCount++;
      }
    });

    const totalUnpaid = totalAmount - totalPaid;
    return {
      totalAmount,
      totalPaid,
      totalUnpaid: totalUnpaid < 0 ? 0 : totalUnpaid,
      overdueCount,
      draftCount,
    };
  }, [invoices]);

  const formatMoney = (v) => Number(v || 0).toFixed(2);

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const goToOrgSettings = (orgId) => {
    if (!orgId) return;
    navigate(`/super/organizations?org_id=${orgId}`);
  };

  // --- PDF URL helper (pretvara apsolutnu putanju u /documents/invoices/...) ---
  const getInvoicePdfUrl = (inv) => {
    if (!inv.pdf_path) return null;
    const idx = inv.pdf_path.indexOf('/documents/invoices/');
    if (idx !== -1) {
      return inv.pdf_path.slice(idx); // npr. "/documents/invoices/1/invoice-2025-1-001.pdf"
    }
    return inv.pdf_path; // ako već dobijaš relativan URL
  };

  // --- NOVO: ZIP EXPORT ---
  const exportZip = async () => {
    try {
      if (!invoices || invoices.length === 0) {
        setError('There are no invoices for the current filters to export.');
        setSuccessMsg('');
        return;
      }

      setExportingZip(true);
      setError('');
      setSuccessMsg('');

      const res = await API.get('/super/invoices/export-zip', {
        params: filters,
        responseType: 'blob',
      });

      // predlog naziva fajla iz Content-Disposition
      const cd = res.headers?.['content-disposition'] || '';
      let filename = 'invoices-export.zip';
      const match = /filename="?([^"]+)"?/.exec(cd);
      if (match && match[1]) {
        filename = match[1];
      }

      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setSuccessMsg('ZIP export generated successfully.');
    } catch (e) {
      console.error('export-zip error', e);
      setError('Failed to export ZIP with invoice PDFs.');
    } finally {
      setExportingZip(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-3 text-sm text-gray-700">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <h2 className="text-base font-semibold mb-1 flex items-center gap-2">
            <FileText size={16} />
            Org Billing & Invoices
          </h2>
          <p className="text-[11px] text-gray-500">
            Per-organization subscription invoices (auto & manual), with PDF generation and email sending.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* NOVO: Export ZIP */}
          <button
            type="button"
            onClick={exportZip}
            disabled={exportingZip || !invoices || invoices.length === 0}
            className="inline-flex items-center gap-1 px-3 py-1 rounded text-xs bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
            title="Download all filtered invoice PDFs as a single ZIP"
          >
            <Download size={14} />
            {exportingZip ? 'Exporting…' : 'Export PDFs (ZIP)'}
          </button>

          <button
            type="button"
            onClick={runAutoBilling}
            disabled={autoRunning}
            className="inline-flex items-center gap-1 px-3 py-1 rounded text-xs bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
          >
            <RefreshCw size={14} className={autoRunning ? 'animate-spin' : ''} />
            {autoRunning ? 'Running…' : 'Run auto billing'}
          </button>
          <button
            type="button"
            onClick={openNewModal}
            className="inline-flex items-center gap-1 px-3 py-1 rounded text-xs bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <PlusCircle size={14} />
            New invoice
          </button>
        </div>
      </div>

      {/* STATS BAR */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
        <div className="bg-slate-50 border border-slate-100 rounded-lg p-2 flex items-center justify-between">
          <div>
            <div className="text-[11px] text-gray-500">Total invoiced (filtered)</div>
            <div className="text-sm font-semibold text-gray-900">
              {formatMoney(stats.totalAmount)} {invoices[0]?.currency || 'USD'}
            </div>
          </div>
          <div className="text-slate-400">
            <FileText size={18} />
          </div>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-2 flex items-center justify-between">
          <div>
            <div className="text-[11px] text-gray-500">Paid</div>
            <div className="text-sm font-semibold text-emerald-800">
              {formatMoney(stats.totalPaid)} {invoices[0]?.currency || 'USD'}
            </div>
          </div>
          <div className="text-emerald-500">
            <CheckCircle size={18} />
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-2 flex items-center justify-between">
          <div>
            <div className="text-[11px] text-gray-500">Unpaid (draft/sent)</div>
            <div className="text-sm font-semibold text-amber-800">
              {formatMoney(stats.totalUnpaid)} {invoices[0]?.currency || 'USD'}
            </div>
          </div>
          <div className="text-amber-500">
            <AlertTriangle size={18} />
          </div>
        </div>
        <div className="bg-red-50 border border-red-100 rounded-lg p-2 flex items-center justify-between">
          <div>
            <div className="text-[11px] text-gray-500">Overdue / Draft count</div>
            <div className="text-sm font-semibold text-red-700">
              {stats.overdueCount} / {stats.draftCount}
            </div>
          </div>
          <div className="text-red-500">
            <Info size={18} />
          </div>
        </div>
      </div>

      {/* FILTERS */}
      <div className="mb-3 bg-slate-50 border border-slate-100 rounded-lg p-2">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1 text-[11px] text-gray-600 font-semibold">
            <Filter size={12} />
            Filters
          </div>
          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border border-gray-200 text-gray-600 hover:bg-gray-100"
          >
            <XCircle size={12} />
            Clear
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Organization</label>
            <select
              name="organization_id"
              value={filters.organization_id}
              onChange={handleFilterChange}
              className="w-full border rounded px-2 py-1 text-xs"
            >
              <option value="">All</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} ({o.slug})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="w-full border rounded px-2 py-1 text-xs"
            >
              <option value="">All</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="canceled">Canceled</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">From (invoice date)</label>
            <input
              type="date"
              name="date_from"
              value={filters.date_from}
              onChange={handleFilterChange}
              className="w-full border rounded px-2 py-1 text-xs"
            />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">To (invoice date)</label>
            <input
              type="date"
              name="date_to"
              value={filters.date_to}
              onChange={handleFilterChange}
              className="w-full border rounded px-2 py-1 text-xs"
            />
          </div>
          <div className="flex items-end">
            <p className="text-[10px] text-gray-500">
              Filters apply instantly when changed (no extra button).
            </p>
          </div>
        </div>
      </div>

      {/* ALERTS */}
      {error && (
        <div className="mb-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 flex items-center gap-1">
          <AlertTriangle size={12} />
          <span>{error}</span>
        </div>
      )}
      {successMsg && (
        <div className="mb-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 flex items-center gap-1">
          <CheckCircle size={12} />
          <span>{successMsg}</span>
        </div>
      )}
      {autoSummary && (
        <div className="mb-2 text-[11px] text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1">
          Auto billing summary: generated {autoSummary.generated || 0} invoices, errors{' '}
          {autoSummary.errors || 0}.
        </div>
      )}

      {/* TABLE */}
      {loading ? (
        <div className="text-xs text-gray-500">Loading invoices…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left py-1 px-2 w-7"></th>
                <th className="text-left py-1 px-2">Invoice</th>
                <th className="text-left py-1 px-2">Organization</th>
                <th className="text-left py-1 px-2">Period</th>
                <th className="text-right py-1 px-2">Amount</th>
                <th className="text-left py-1 px-2">Status</th>
                <th className="text-left py-1 px-2">Due</th>
                <th className="text-left py-1 px-2">Paid</th>
                <th className="text-left py-1 px-2">PDF</th>
                <th className="text-left py-1 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 && (
                <tr>
                  <td colSpan={10} className="py-2 px-2 text-gray-500">
                    No invoices yet.
                  </td>
                </tr>
              )}
              {invoices.map((inv) => {
                const isExpanded = expandedId === inv.id;
                const currency = inv.currency || 'USD';
                const hasBillingEmail = orgEmailMap[inv.organization_id] ?? true;
                const emailInfo = orgEmailDetails[inv.organization_id] || {};
                const billingEmail = emailInfo.billing;
                const contactEmail = emailInfo.contact;
                const sendTo = billingEmail || contactEmail || '';

                let sendTooltip = 'No billing or contact email defined';
                if (sendTo) {
                  if (
                    billingEmail &&
                    contactEmail &&
                    billingEmail !== contactEmail
                  ) {
                    sendTooltip = `Send to: ${billingEmail} (billing) or ${contactEmail} (contact)`;
                  } else {
                    sendTooltip = `Send to: ${sendTo}`;
                  }
                }

                return (
                  <React.Fragment key={inv.id}>
                    <tr className="border-b last:border-0">
                      <td className="py-1 px-2 align-top">
                        <button
                          type="button"
                          onClick={() => toggleExpand(inv.id)}
                          className="text-gray-500 hover:text-gray-800"
                          title={isExpanded ? 'Hide details' : 'Show details'}
                        >
                          {isExpanded ? (
                            <ChevronDown size={14} />
                          ) : (
                            <ChevronRight size={14} />
                          )}
                        </button>
                      </td>
                      <td className="py-1 px-2 align-top">
                        <div className="font-semibold text-gray-800">{inv.invoice_number}</div>
                        <div className="text-[10px] text-gray-400 flex items-center gap-1">
                          <span>{inv.invoice_date}</span>
                          {inv.invoice_type && (
                            <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[9px]">
                              {inv.invoice_type}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-1 px-2 align-top">
                        <div className="text-gray-800 flex items-center gap-1">
                          <span>{inv.organization_name}</span>
                          {!hasBillingEmail && (
                            <span
                              className="px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 text-[9px] border border-red-200"
                              title="No billing_email/contact_email is set for this organization."
                            >
                              Missing email
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-gray-400">
                          {inv.organization_slug} · plan {inv.organization_plan}
                        </div>
                      </td>
                      <td className="py-1 px-2 text-gray-700 align-top">
                        {inv.period_start} – {inv.period_end}
                      </td>
                      <td className="py-1 px-2 text-right text-gray-800 align-top">
                        {formatMoney(inv.amount)} {currency}
                      </td>
                      <td className="py-1 px-2 align-top">
                        <span
                          className={
                            'px-2 py-0.5 rounded-full text-[10px] ' +
                            (inv.status === 'paid'
                              ? 'bg-emerald-100 text-emerald-700'
                              : inv.status === 'overdue'
                              ? 'bg-red-100 text-red-700'
                              : inv.status === 'sent'
                              ? 'bg-sky-100 text-sky-700'
                              : inv.status === 'canceled'
                              ? 'bg-gray-100 text-gray-500'
                              : 'bg-amber-100 text-amber-700')
                          }
                        >
                          {inv.status}
                        </span>
                      </td>
                      <td className="py-1 px-2 text-gray-700 align-top">
                        {inv.due_date || '-'}
                      </td>
                      <td className="py-1 px-2 text-gray-700 align-top">
                        {inv.paid_date || '-'}
                      </td>
                      <td className="py-1 px-2 align-top">
                        {inv.pdf_path ? (
                          <button
                            type="button"
                            onClick={() => {
                              const url = getInvoicePdfUrl(inv);
                              if (url) {
                                window.open(url, '_blank', 'noopener,noreferrer');
                              }
                            }}
                            className="text-[11px] text-sky-700 underline hover:text-sky-900"
                            title="Open invoice PDF in a new tab"
                          >
                            PDF ready
                          </button>
                        ) : (
                          <span className="text-[11px] text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-1 px-2 align-top">
                        <div className="flex items-center gap-1 flex-wrap">
                          {hasBillingEmail ? (
                            <button
                              type="button"
                              onClick={() => sendEmail(inv.id)}
                              disabled={sendingId === inv.id}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
                              title={sendTooltip}
                            >
                              <Send size={12} />
                              {sendingId === inv.id ? 'Sending…' : 'Send'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => goToOrgSettings(inv.organization_id)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                              title="Open organization settings to set billing email"
                            >
                              <AlertTriangle size={12} />
                              Missing email
                            </button>
                          )}

                          {inv.status !== 'paid' && (
                            <button
                              type="button"
                              onClick={() => markPaid(inv.id)}
                              disabled={markingPaidId === inv.id}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                            >
                              <CheckCircle size={12} />
                              {markingPaidId === inv.id ? 'Saving…' : 'Mark paid'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded row – breakdown */}
                    {isExpanded && (
                      <tr className="border-b last:border-0 bg-slate-50/70">
                        <td colSpan={10} className="py-2 px-3">
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                            {/* LEFT – breakdown */}
                            <div className="space-y-1 text-[11px] text-gray-700">
                              <div className="font-semibold text-[11px] text-gray-800 mb-1">
                                Billing breakdown
                              </div>
                              <div>
                                <span className="text-gray-500">Cycle:</span>{' '}
                                <span className="font-medium">{inv.billing_cycle || 'monthly'}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Teachers:</span>{' '}
                                <span className="font-medium">{inv.teachers_count || 0}</span>{' '}
                                <span className="text-gray-400">
                                  (fee {formatMoney(inv.teachers_fee || 0)} {currency})
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500">Students:</span>{' '}
                                <span className="font-medium">{inv.students_count || 0}</span>{' '}
                                <span className="text-gray-400">
                                  (fee {formatMoney(inv.students_fee || 0)} {currency})
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500">Base fee:</span>{' '}
                                <span className="font-medium">
                                  {formatMoney(inv.base_fee || 0)} {currency}
                                </span>
                              </div>
                              {inv.other_fee && Number(inv.other_fee) !== 0 && (
                                <div>
                                  <span className="text-gray-500">Other:</span>{' '}
                                  <span className="font-medium">
                                    {formatMoney(inv.other_fee)} {currency}
                                  </span>
                                </div>
                              )}
                              <div className="mt-1">
                                <span className="text-gray-500">Total:</span>{' '}
                                <span className="font-semibold">
                                  {formatMoney(inv.amount)} {currency}
                                </span>
                              </div>
                            </div>

                            {/* RIGHT – notes */}
                            <div className="flex-1 text-[11px] text-gray-700">
                              <div className="flex items-center gap-1 mb-1">
                                <Info size={12} className="text-slate-500" />
                                <span className="font-semibold text-[11px] text-gray-800">
                                  Notes
                                </span>
                              </div>
                              <div className="border border-slate-200 rounded-md p-2 bg-white">
                                {inv.notes ? (
                                  <p className="whitespace-pre-wrap">{inv.notes}</p>
                                ) : (
                                  <p className="text-gray-400 italic">No notes.</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* New invoice modal */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg text-sm max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-3 py-2 border-b">
              <h3 className="font-semibold text-sm flex items-center gap-1">
                <PlusCircle size={14} />
                New invoice
              </h3>
              <button
                type="button"
                onClick={() => setShowNewModal(false)}
                className="text-gray-500 hover:text-gray-800 text-lg"
              >
                ✕
              </button>
            </div>
            <form onSubmit={submitNewInvoice} className="p-3 space-y-2 overflow-y-auto">
              <div>
                <label className="block text-[11px] text-gray-500 mb-1">Organization</label>
                <select
                  name="organization_id"
                  value={newForm.organization_id}
                  onChange={handleNewChange}
                  className="w-full border rounded px-2 py-1 text-xs"
                  required
                >
                  <option value="">Select organization…</option>
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name} ({o.slug})
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">Period start</label>
                  <input
                    type="date"
                    name="period_start"
                    value={newForm.period_start}
                    onChange={handleNewChange}
                    className="w-full border rounded px-2 py-1 text-xs"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">Period end</label>
                  <input
                    type="date"
                    name="period_end"
                    value={newForm.period_end}
                    onChange={handleNewChange}
                    className="w-full border rounded px-2 py-1 text-xs"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-[11px] text-gray-500 mb-1">Type</label>
                <select
                  name="invoice_type"
                  value={newForm.invoice_type}
                  onChange={handleNewChange}
                  className="w-full border rounded px-2 py-1 text-xs"
                >
                  <option value="subscription">Subscription</option>
                  <option value="one_off">One-off</option>
                  <option value="credit">Credit</option>
                </select>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="checkbox"
                  id="auto_calculate"
                  name="auto_calculate"
                  checked={newForm.auto_calculate}
                  onChange={handleNewChange}
                />
                <label htmlFor="auto_calculate" className="text-[11px] text-gray-600">
                  Auto-calculate amount based on plan, active teachers and students
                </label>
              </div>
              {!newForm.auto_calculate && (
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">
                    Amount (manual override)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="amount"
                    value={newForm.amount}
                    onChange={handleNewChange}
                    className="w-full border rounded px-2 py-1 text-xs"
                  />
                </div>
              )}
              <div>
                <label className="block text-[11px] text-gray-500 mb-1">Notes (optional)</label>
                <textarea
                  name="notes"
                  value={newForm.notes}
                  onChange={handleNewChange}
                  className="w-full border rounded px-2 py-1 text-xs"
                  rows={3}
                  placeholder="E.g. Additional discount, manual correction, etc."
                />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => setShowNewModal(false)}
                  className="px-3 py-1 rounded text-xs border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-3 py-1 rounded text-xs bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {creating ? 'Saving…' : 'Create invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperBilling;







